import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { task } from 'ember-concurrency';

/**
 * Service for sh-payment related API calls
 */
export default Service.extend({
  api: service(),
  store: service(),
  appCache: service(),
  shData: service(),

  /**
   * Approve an advance
   *
   * @param {ShPayment} shPayment
   */
  agreeAdvance: task(function *(shPayment) {
    const options = {};

    this.shData.addIncludesToOptions(options, []);

    yield this.get('api').post(`sh-payments/${shPayment.get('id')}/agree`, options);
  }),

  /**
   * Cancel expedite on a payment
   *
   * @param shPayment
   * @param opt_includes
   * @returns {Promise<ShPayment>}
   */
  cancelExpeditePayment(shPayment, opt_includes = [])
  {
    const options = {};

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.get('api').post(`sh-payments/${shPayment.get('id')}/cancel-expedite`, options)
      .then(response => {
        this.shData.pushResponseToStore(response);
        return this.get('store').peekRecord('shPayment', response.data.id);
      });
  },

  /**
   * Expedite a payment
   *
   * @param shPayment
   * @param opt_includes
   * @returns {Promise<ShPayment>}
   */
  expeditePayment(shPayment, opt_includes = [])
  {
    const options = {};

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.get('api').post(`sh-payments/${shPayment.get('id')}/expedite`, options)
      .then(response => {
        this.shData.pushResponseToStore(response);
        return this.get('store').peekRecord('shPayment', response.data.id);
      });
  },

  /**
   * Take over fee of expediting a payment
   *
   * @param shPayment
   * @param opt_includes
   * @returns {Promise<ShPayment>}
   */
  expeditePaymentTakeoverFees(shPayment, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({
      jsonData: {
        takeover_expedite_fee: true,
      },
    }, opt_includes);

    return this.api.post(`sh-payments/${shPayment.id}/expedite`, options)
      .then(response => {
        this.shData.pushResponseToStore(response);
        return this.store.peekRecord('shPayment', response.data.id);
      });
  },

  /**
   * @param {String} shPaymentId
   * @param {String[]} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadShPayment(shPaymentId, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`sh-payments/${shPaymentId}`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shPayment = this.get('store').peekRecord('shPayment', response.data.id);
        resolve(shPayment);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error loading the payment.  Please refresh and try again.',
          'loadShPayment -- error loading payment id : ' + shPaymentId,
        );
        reject(e);
      });
    });
  },

  /**
   * Load sh payments from the server
   *
   * @param {Number=} opt_page
   * @param {Number=} opt_count
   * @param {Object=} opt_filter
   * @param {String[]=} opt_includes
   * @param {Date=} opt_startAt
   * @param {Date=} opt_endAt
   * @param {Boolean=} opt_isSkipCache
   * @return {Ember.RSVP.Promise}
   */
  loadShPayments(
  opt_page = 1,
  opt_count = 10,
  opt_filter = {},
  opt_includes = [],
  opt_startAt = null, // TODO - THERE IS A BUG WITH THE CACHING OF THESE DATES, NOT RESPECTING THEM.
  opt_endAt = null,
  opt_isSkipCache = false) {
    let responseObjectFromCache = this.appCache.getCachedModelArrayForRequest(this, 'loadShPayments', arguments);
    if (!opt_isSkipCache && responseObjectFromCache) {
      return Promise.resolve(responseObjectFromCache);
    }

    const options = {
      params: {
        page: opt_page,
        count: opt_count,
        filter: opt_filter
      }
    };

    if (opt_startAt) {
      options.params['start_at'] = opt_startAt.toISOString();
    }

    if (opt_endAt) {
      options.params['end_at'] = opt_endAt.toISOString();
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`sh-payments/`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let shPayments = this.shData.getModelArrayFromResponse(response, 'shPayment'),
          responseObject = {
            shPayments: shPayments,
            pagination: this.shData.getPaginationModelFromResponse(response)
          };

        this.appCache.setCachedModelArrayForRequest(
          this,
          'loadShPayments',
          arguments,
          responseObject);

        resolve(responseObject);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error loading these payments. Please refresh and try again.',
          'loadShPayments -- error loading payments',
        );
        reject(e);
      });
    });
  },

  /**
   * Release a payment, i.e. pay the user
   *
   * @param {ShPayment} shPayment
   * @return {Ember.RSVP.Promise}
   */
  releasePayment(shPayment) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').put(`sh-payments/${shPayment.get('id')}/release`, options).then(response => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error loading the payment.  Please refresh and try again.',
          'releasePayment -- error releasing payment id : ' + shPayment.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Updates the FeeStructure for a given ShPayment
   *
   * @param {DS.Model} shPayment
   * @param {Object} feeStructureJSON
   * @returns {Promise} API call to update the application FeeStructure
   */
  updateFeeStructure(shPayment, feeStructureJSON) {
    const shPaymentId = shPayment.get('id');
    const endpoint = `sh-payments/${shPaymentId}/fee-structure`;

    const options = {
      jsonData: feeStructureJSON
    };

    return this.get('api').put(endpoint, options);
  },
});
